import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Info, MousePointerClick } from 'lucide-react';
import type { IncidentType, EditableSegment } from '../types';
import { incidentTypes, type ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { cn } from '@/lib/utils';

interface IncidentDetailsSectionProps {
  incidentType: IncidentType;
  description: string;
  affectedSegmentId: string | null;
  segments: EditableSegment[];
  onDescriptionChange: (value: string) => void;
  onContinue: () => void;
  onBack: () => void;
  theme?: ClaimTheme;
}

export function IncidentDetailsSection({
  incidentType,
  description,
  affectedSegmentId,
  segments,
  onDescriptionChange,
  onContinue,
  onBack,
  theme = 'invol',
}: IncidentDetailsSectionProps) {
  const config = incidentTypes.find((t) => t.id === incidentType);
  const hasMultipleSegments = segments.length > 1;
  const hasSelectedSegment = affectedSegmentId !== null;

  if (!config) return null;

  return (
    <motion.div
      key="incident-details"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col"
    >
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="self-start -ml-2 mb-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Incident type badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'inline-flex items-center self-start px-3 py-1.5 rounded-full text-sm font-medium mb-4',
          incidentType === 'delay' && 'bg-amber-100 text-amber-700',
          incidentType === 'cancel' && 'bg-red-100 text-red-700',
          incidentType === 'denied_boarding' && 'bg-orange-100 text-orange-700',
          incidentType === 'other' && 'bg-slate-100 text-slate-700'
        )}
      >
        {config.label}
      </motion.div>

      {/* Flight selection prompt */}
      {hasMultipleSegments && !hasSelectedSegment && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Welcher Flug war betroffen?
          </h2>
          <div className="flex items-center gap-2 text-slate-500">
            <MousePointerClick className="w-5 h-5 text-blue-500" />
            <p>Klicken Sie auf einen Flug links, um ihn auszuwählen</p>
          </div>
        </motion.div>
      )}

      {/* Description section */}
      {(!hasMultipleSegments || hasSelectedSegment) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="space-y-4"
        >
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Erzählen Sie uns mehr
            </h2>
            <label className="block text-sm font-medium text-slate-600 mb-3">
              {config.followUpQuestion}
            </label>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Beschreiben Sie mit Ihren eigenen Worten, was passiert ist..."
              rows={5}
              className={cn(
                'w-full px-4 py-3 rounded-xl border border-slate-200',
                'text-slate-800 placeholder:text-slate-400',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400',
                'transition-all resize-none text-base'
              )}
            />
            <p className="text-xs text-slate-500 mt-2">
              Je mehr Details Sie angeben, desto besser können wir Ihren Anspruch bewerten.
            </p>
          </div>

          {/* Help tips */}
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ delay: 0.25 }}
            className="rounded-xl bg-blue-50 border border-blue-100 p-4"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">
                  Hilfreiche Informationen:
                </p>
                <ul className="text-sm text-blue-600 space-y-1">
                  {config.helpItems.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Continue button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-end pt-4"
          >
            <AnimatedButton
              variant="primary"
              size="lg"
              onClick={onContinue}
              icon={<ArrowRight className="w-5 h-5" />}
              theme={theme}
            >
              Weiter
            </AnimatedButton>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}
